import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type {
  FormTemplateSummary,
  FormTemplateDetail,
  FormTemplateDiffResult,
  FormTemplateExportData,
  FormSectionCreateInput,
  FormSectionUpdatePayload,
  FormFieldUpdatePayload,
  FormFieldOption,
  FormFieldType,
  FormTemplateScope,
  FormTemplateCreatePayload,
  FormTemplateUpdatePayload,
  FormTemplateVersionCreatePayload,
  FormTemplatePublishOptions,
  FormTemplateVersionDetail,
} from '@/types'
import { FormVersionStatus } from '@/types'
import type { ApiResponse } from '@/types'
import * as formTemplateApi from '../../services/api/formTemplates'
import type {
  CreateLogicRulePayload,
  UpdateLogicRulePayload,
} from '../../services/api/formTemplates'

interface FetchTemplatesArgs {
  scopeType?: FormTemplateScope | string
  projectId?: string
  includeArchived?: boolean
  search?: string
}

export interface FormTemplateState {
  templates: FormTemplateSummary[]
  loading: boolean
  error: string | null
  activeTemplate: FormTemplateDetail | null
  diff: FormTemplateDiffResult | null
  exportData: FormTemplateExportData | null
}

const initialState: FormTemplateState = {
  templates: [],
  loading: false,
  error: null,
  activeTemplate: null,
  diff: null,
  exportData: null,
}

const detailToSummary = (detail: FormTemplateDetail): FormTemplateSummary => {
  const latest = detail.versions[0]
    ? {
        id: detail.versions[0].id,
        versionNumber: detail.versions[0].versionNumber,
        status: detail.versions[0].status,
        displayName: detail.versions[0].displayName ?? null,
        description: detail.versions[0].description ?? null,
        createdAt: detail.versions[0].createdAt ?? new Date().toISOString(),
        updatedAt: detail.versions[0].updatedAt ?? new Date().toISOString(),
      }
    : undefined

  return {
    id: detail.id,
    name: detail.name,
    scopeType: detail.scopeType,
    formType: detail.formType,
    projectId: detail.projectId ?? null,
    ownerId: detail.ownerId ?? null,
    tags: detail.tags ?? [],
    isPublished: detail.isPublished ?? false,
    archived: detail.archived ?? false,
    description: detail.description ?? null,
    activationStartDate: detail.activationStartDate ?? null,
    activationEndDate: detail.activationEndDate ?? null,
    isAlwaysActive: detail.isAlwaysActive ?? false,
    latestVersion: latest,
  }
}

const updateSummaryList = (summaries: FormTemplateSummary[], summary: FormTemplateSummary) => {
  const filtered = summaries.filter(item => item.id !== summary.id)
  return [summary, ...filtered]
}

const resolveErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message
    if (typeof message === 'string' && message.length > 0) {
      return message
    }
  }
  return fallback
}

export const fetchFormTemplates = createAsyncThunk(
  'formTemplates/fetchList',
  async (params: FetchTemplatesArgs | undefined, thunkAPI) => {
    try {
      const response = await formTemplateApi.getTemplates(params)
      return response.data ?? []
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '取得表單模板失敗'))
    }
  },
)

export const fetchFormTemplateDetail = createAsyncThunk(
  'formTemplates/fetchDetail',
  async (templateId: string, thunkAPI) => {
    try {
      const response = await formTemplateApi.getTemplateDetail(templateId)
      return response.data as FormTemplateDetail
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '取得表單模板詳情失敗'))
    }
  },
)

export const fetchTemplateDiff = createAsyncThunk(
  'formTemplates/fetchDiff',
  async (
    args: { templateId: string; versionId: string; compareTo: string },
    thunkAPI,
  ) => {
    try {
      const response = await formTemplateApi.diffTemplateVersions(
        args.templateId,
        args.versionId,
        args.compareTo,
      )
      return response as ApiResponse<FormTemplateDiffResult>
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '比較版本差異失敗'))
    }
  },
)

export const exportTemplate = createAsyncThunk(
  'formTemplates/exportVersion',
  async (
    args: { templateId: string; versionId: string },
    thunkAPI,
  ) => {
    try {
      const response = await formTemplateApi.exportTemplateVersion(args.templateId, args.versionId)
      return response.data as FormTemplateExportData
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '匯出模板失敗'))
    }
  },
)

export const importTemplate = createAsyncThunk(
  'formTemplates/importTemplate',
  async (payload: FormTemplateExportData, thunkAPI) => {
    try {
      const response = await formTemplateApi.importTemplate(payload)
      return response.data?.template as FormTemplateSummary
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '匯入模板失敗'))
    }
  },
)

export const createTemplate = createAsyncThunk(
  'formTemplates/createTemplate',
  async (payload: FormTemplateCreatePayload, thunkAPI) => {
    try {
      const response = await formTemplateApi.createTemplate(payload)
      return response.data as FormTemplateDetail
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '建立模板失敗'))
    }
  },
)

export const updateTemplateMeta = createAsyncThunk(
  'formTemplates/updateTemplateMeta',
  async (
    args: { templateId: string; data: FormTemplateUpdatePayload },
    thunkAPI,
  ) => {
    try {
      const response = await formTemplateApi.updateTemplate(args.templateId, args.data)
      return response.data as FormTemplateSummary
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '更新模板資訊失敗'))
    }
  },
)

export const deleteTemplate = createAsyncThunk(
  'formTemplates/deleteTemplate',
  async (templateId: string, thunkAPI) => {
    try {
      await formTemplateApi.deleteTemplate(templateId)
      return templateId
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '刪除模板失敗'))
    }
  },
)

export const createTemplateVersion = createAsyncThunk(
  'formTemplates/createTemplateVersion',
  async (
    args: { templateId: string; data: FormTemplateVersionCreatePayload },
    thunkAPI,
  ) => {
    try {
      const response = await formTemplateApi.createTemplateVersion(args.templateId, args.data)
      return response.data as FormTemplateVersionDetail
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '建立新版本失敗'))
    }
  },
)

export const publishTemplateVersion = createAsyncThunk(
  'formTemplates/publishTemplateVersion',
  async (
    args: { templateId: string; versionId: string; options: FormTemplatePublishOptions },
    thunkAPI,
  ) => {
    try {
      const response = await formTemplateApi.publishTemplateVersion(
        args.templateId,
        args.versionId,
        args.options,
      )
      return response.data as FormTemplateVersionDetail
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '發佈版本失敗'))
    }
  },
)

export const createTemplateSection = createAsyncThunk(
  'formTemplates/createSection',
  async (
    args: { templateId: string; versionId: string; data: FormSectionCreateInput },
    thunkAPI,
  ) => {
    try {
      await formTemplateApi.createSection(args.templateId, args.versionId, args.data)
      const detail = await thunkAPI.dispatch(fetchFormTemplateDetail(args.templateId)).unwrap()
      return detail
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '新增節點失敗'))
    }
  },
)

export const updateTemplateSection = createAsyncThunk(
  'formTemplates/updateSection',
  async (
    args: { templateId: string; versionId: string; sectionId: string; data: FormSectionUpdatePayload },
    thunkAPI,
  ) => {
    try {
      await formTemplateApi.updateSection(args.templateId, args.versionId, args.sectionId, args.data)
      const detail = await thunkAPI.dispatch(fetchFormTemplateDetail(args.templateId)).unwrap()
      return detail
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '更新節點失敗'))
    }
  },
)

export const deleteTemplateSection = createAsyncThunk(
  'formTemplates/deleteSection',
  async (
    args: { templateId: string; versionId: string; sectionId: string },
    thunkAPI,
  ) => {
    try {
      await formTemplateApi.deleteSection(args.templateId, args.versionId, args.sectionId)
      const detail = await thunkAPI.dispatch(fetchFormTemplateDetail(args.templateId)).unwrap()
      return detail
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '刪除節點失敗'))
    }
  },
)

interface CreateFieldArgs {
  templateId: string
  versionId: string
  data: {
    key: string
    label: string
    type: FormFieldType
    description?: string | null
    isRequired?: boolean
    isRepeatable?: boolean
    placeholder?: string | null
    helpText?: string | null
    defaultValue?: unknown
    metadata?: Record<string, unknown>
    category?: string | null
    subcategory?: string | null
    position?: number
    sectionId?: string | null
    options?: FormFieldOption[]
  }
}

export const createTemplateField = createAsyncThunk(
  'formTemplates/createField',
  async ({ templateId, versionId, data }: CreateFieldArgs, thunkAPI) => {
    try {
      await formTemplateApi.createField(templateId, versionId, data)
      const detail = await thunkAPI.dispatch(fetchFormTemplateDetail(templateId)).unwrap()
      return detail
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '新增欄位失敗'))
    }
  },
)

export const updateTemplateField = createAsyncThunk(
  'formTemplates/updateField',
  async (
    args: { templateId: string; versionId: string; fieldId: string; data: FormFieldUpdatePayload },
    thunkAPI,
  ) => {
    try {
      await formTemplateApi.updateField(args.templateId, args.versionId, args.fieldId, args.data)
      const detail = await thunkAPI.dispatch(fetchFormTemplateDetail(args.templateId)).unwrap()
      return detail
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '更新欄位失敗'))
    }
  },
)

export const deleteTemplateField = createAsyncThunk(
  'formTemplates/deleteField',
  async (
    args: { templateId: string; versionId: string; fieldId: string },
    thunkAPI,
  ) => {
    try {
      await formTemplateApi.deleteField(args.templateId, args.versionId, args.fieldId)
      const detail = await thunkAPI.dispatch(fetchFormTemplateDetail(args.templateId)).unwrap()
      return detail
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '刪除欄位失敗'))
    }
  },
)

export const createTemplateLogicRule = createAsyncThunk(
  'formTemplates/createLogicRule',
  async (
    args: { templateId: string; versionId: string; data: CreateLogicRulePayload },
    thunkAPI,
  ) => {
    try {
      await formTemplateApi.createLogicRule(args.templateId, args.versionId, args.data)
      const detail = await thunkAPI.dispatch(fetchFormTemplateDetail(args.templateId)).unwrap()
      return detail
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '新增條件邏輯失敗'))
    }
  },
)

export const updateTemplateLogicRule = createAsyncThunk(
  'formTemplates/updateLogicRule',
  async (
    args: {
      templateId: string
      versionId: string
      ruleId: string
      data: UpdateLogicRulePayload
    },
    thunkAPI,
  ) => {
    try {
      await formTemplateApi.updateLogicRule(args.templateId, args.versionId, args.ruleId, args.data)
      const detail = await thunkAPI.dispatch(fetchFormTemplateDetail(args.templateId)).unwrap()
      return detail
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '更新條件邏輯失敗'))
    }
  },
)

export const deleteTemplateLogicRule = createAsyncThunk(
  'formTemplates/deleteLogicRule',
  async (
    args: { templateId: string; versionId: string; ruleId: string },
    thunkAPI,
  ) => {
    try {
      await formTemplateApi.deleteLogicRule(args.templateId, args.versionId, args.ruleId)
      const detail = await thunkAPI.dispatch(fetchFormTemplateDetail(args.templateId)).unwrap()
      return detail
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '刪除條件邏輯失敗'))
    }
  },
)

export const reorderTemplateLogicRules = createAsyncThunk(
  'formTemplates/reorderLogicRules',
  async (
    args: { templateId: string; versionId: string; orderedRuleIds: string[] },
    thunkAPI,
  ) => {
    try {
      await formTemplateApi.reorderLogicRules(
        args.templateId,
        args.versionId,
        args.orderedRuleIds,
      )
      const detail = await thunkAPI.dispatch(fetchFormTemplateDetail(args.templateId)).unwrap()
      return detail
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '條件邏輯排序失敗'))
    }
  },
)

export const reorderTemplateFields = createAsyncThunk(
  'formTemplates/reorderFields',
  async (
    args: { templateId: string; versionId: string; orderedIds: string[] },
    thunkAPI,
  ) => {
    try {
      await Promise.all(
        args.orderedIds.map((fieldId, position) =>
          formTemplateApi.updateField(args.templateId, args.versionId, fieldId, {
            position,
          }),
        ),
      )
      const detail = await thunkAPI.dispatch(fetchFormTemplateDetail(args.templateId)).unwrap()
      return detail
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(resolveErrorMessage(error, '欄位排序更新失敗'))
    }
  },
)

const formTemplateSlice = createSlice({
  name: 'formTemplates',
  initialState,
  reducers: {
    clearTemplateError(state) {
      state.error = null
    },
    clearTemplateDiff(state) {
      state.diff = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFormTemplates.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchFormTemplates.fulfilled, (state, action) => {
        state.loading = false
        state.templates = action.payload
      })
      .addCase(fetchFormTemplates.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload as string) || action.error.message || '載入模板失敗'
      })
      .addCase(fetchFormTemplateDetail.fulfilled, (state, action) => {
        state.activeTemplate = action.payload
      })
      .addCase(fetchTemplateDiff.fulfilled, (state, action) => {
        state.diff = (action.payload as ApiResponse<FormTemplateDiffResult>).data || null
      })
      .addCase(exportTemplate.fulfilled, (state, action) => {
        state.exportData = action.payload
      })
      .addCase(importTemplate.fulfilled, (state, action) => {
        state.templates = [action.payload, ...state.templates]
      })
      .addCase(createTemplate.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createTemplate.fulfilled, (state, action) => {
        state.loading = false
        state.activeTemplate = action.payload
        const summary = detailToSummary(action.payload)
        state.templates = updateSummaryList(state.templates, summary)
      })
      .addCase(createTemplate.rejected, (state, action) => {
        state.loading = false
        state.error = (action.payload as string) || action.error.message || '建立模板失敗'
      })
      .addCase(updateTemplateMeta.fulfilled, (state, action) => {
        const summary = action.payload
        const input = (action.meta as { arg: { data: FormTemplateUpdatePayload } }).arg.data
        state.templates = updateSummaryList(state.templates, summary)
        if (state.activeTemplate && state.activeTemplate.id === summary.id) {
          state.activeTemplate = {
            ...state.activeTemplate,
            name: summary.name,
            description:
              input.description !== undefined ? input.description ?? null : state.activeTemplate.description,
            tags: summary.tags,
            projectId: summary.projectId ?? undefined,
            ownerId: summary.ownerId ?? undefined,
            archived: summary.archived,
            isPublished: summary.isPublished,
          }
        }
      })
      .addCase(updateTemplateMeta.rejected, (state, action) => {
        state.error = (action.payload as string) || action.error.message || '更新模板資訊失敗'
      })
      .addCase(deleteTemplate.pending, (state) => {
        state.error = null
      })
      .addCase(deleteTemplate.fulfilled, (state, action) => {
        const removedId = action.payload as string
        state.templates = state.templates.filter(template => template.id !== removedId)
        if (state.activeTemplate?.id === removedId) {
          state.activeTemplate = null
        }
      })
      .addCase(deleteTemplate.rejected, (state, action) => {
        state.error = (action.payload as string) || action.error.message || '刪除模板失敗'
      })
      .addCase(createTemplateVersion.fulfilled, (state, action) => {
        const current = state.activeTemplate
        if (!current) return
        const version = action.payload
        const nextVersions = [version, ...current.versions.filter(v => v.id !== version.id)].sort(
          (a, b) => b.versionNumber - a.versionNumber,
        )
        state.activeTemplate = { ...current, versions: nextVersions }
        const summary = detailToSummary(state.activeTemplate)
        state.templates = updateSummaryList(state.templates, summary)
      })
      .addCase(createTemplateVersion.rejected, (state, action) => {
        state.error = (action.payload as string) || action.error.message || '建立新版本失敗'
      })
      .addCase(publishTemplateVersion.fulfilled, (state, action) => {
        const current = state.activeTemplate
        if (!current) return
        const published = action.payload
        const versions = current.versions.map(version => {
          if (version.id === published.id) {
            return published
          }
          if (published.status === FormVersionStatus.PUBLISHED && version.status === FormVersionStatus.PUBLISHED) {
            return { ...version, status: FormVersionStatus.ARCHIVED }
          }
          return version
        })
        state.activeTemplate = {
          ...current,
          versions,
          isPublished: true,
          archived: false,
        }
        const summary = detailToSummary(state.activeTemplate)
        state.templates = updateSummaryList(state.templates, summary)
      })
      .addCase(publishTemplateVersion.rejected, (state, action) => {
        state.error = (action.payload as string) || action.error.message || '發佈版本失敗'
      })
      .addMatcher(
        (action) =>
          action.type.startsWith('formTemplates/') &&
          action.type.endsWith('/fulfilled') &&
          ['createSection', 'updateSection', 'deleteSection', 'createField', 'updateField', 'deleteField', 'reorderFields', 'createLogicRule', 'updateLogicRule', 'deleteLogicRule', 'reorderLogicRules'].some(keyword => action.type.includes(keyword)),
        (state, action) => {
          state.activeTemplate = action.payload as FormTemplateDetail
        },
      )
      .addMatcher(
        (action) => action.type.startsWith('formTemplates/') && action.type.endsWith('/rejected'),
        (state, action) => {
          state.error = (action.payload as string) || action.error.message || '操作失敗'
          state.loading = false
        },
      )
  },
})

export const { clearTemplateError, clearTemplateDiff } = formTemplateSlice.actions
export default formTemplateSlice.reducer
